import React from "react";

/* ------------------------------------------------------------------ */
/*  Decorative SVG shapes — La Fleur editorial home page               */
/*  Every shape is pointer-events:none via .lf-decor                   */
/* ------------------------------------------------------------------ */

export const Daisy = ({ color = "var(--lf-pink)", size = 90, className = "", style = {} }) => (
  <svg
    viewBox="0 0 100 100"
    width={size}
    height={size}
    className={className}
    style={style}
    aria-hidden="true"
  >
    <g fill={color}>
      <ellipse cx="50" cy="22" rx="14" ry="20" />
      <ellipse cx="78" cy="50" rx="20" ry="14" />
      <ellipse cx="50" cy="78" rx="14" ry="20" />
      <ellipse cx="22" cy="50" rx="20" ry="14" />
      <ellipse cx="70" cy="30" rx="14" ry="18" transform="rotate(45 70 30)" />
      <ellipse cx="70" cy="70" rx="14" ry="18" transform="rotate(-45 70 70)" />
      <ellipse cx="30" cy="70" rx="14" ry="18" transform="rotate(45 30 70)" />
      <ellipse cx="30" cy="30" rx="14" ry="18" transform="rotate(-45 30 30)" />
    </g>
    <circle cx="50" cy="50" r="12" fill="var(--lf-gold-deep)" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Elegant painterly florals — bigger, layered, gradient-filled      */
/* ------------------------------------------------------------------ */
export const PeonyBloom = ({ color = "var(--lf-pink)", accent = "var(--lf-pink-deep)", size = 140, className = "", style = {} }) => {
  const gid = `peony-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <svg viewBox="0 0 120 120" width={size} height={size} className={className} style={style} aria-hidden="true">
      <defs>
        <radialGradient id={gid} cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="white" stopOpacity="0.9" />
          <stop offset="55%" stopColor={color} />
          <stop offset="100%" stopColor={accent} />
        </radialGradient>
      </defs>
      <g fill={`url(#${gid})`}>
        {[0, 60, 120, 180, 240, 300].map((deg) => (
          <path
            key={deg}
            d="M60 20 C 75 20, 82 40, 78 55 C 74 68, 60 70, 60 70 C 60 70, 46 68, 42 55 C 38 40, 45 20, 60 20 Z"
            transform={`rotate(${deg} 60 60)`}
          />
        ))}
        {[30, 90, 150, 210, 270, 330].map((deg) => (
          <path
            key={deg}
            d="M60 35 C 70 35, 74 48, 71 58 C 68 66, 60 68, 60 68 C 60 68, 52 66, 49 58 C 46 48, 50 35, 60 35 Z"
            transform={`rotate(${deg} 60 60)`}
            fillOpacity="0.85"
          />
        ))}
      </g>
      <circle cx="60" cy="60" r="9" fill={accent} opacity="0.7" />
      <circle cx="60" cy="60" r="4" fill="var(--lf-gold-bright)" />
    </svg>
  );
};

export const RoseBud = ({ color = "var(--lf-pink-deep)", size = 90, className = "", style = {} }) => {
  const gid = `rose-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <svg viewBox="0 0 100 120" width={size * 0.83} height={size} className={className} style={style} aria-hidden="true">
      <defs>
        <radialGradient id={gid} cx="50%" cy="35%" r="70%">
          <stop offset="0%" stopColor="white" stopOpacity="0.85" />
          <stop offset="45%" stopColor={color} stopOpacity="0.6" />
          <stop offset="100%" stopColor={color} />
        </radialGradient>
      </defs>
      <path d="M50 90 C 30 90, 15 75, 20 55 C 25 40, 40 30, 50 32 C 60 30, 75 40, 80 55 C 85 75, 70 90, 50 90 Z" fill={`url(#${gid})`} />
      <path d="M50 82 C 38 82, 30 72, 33 60 C 36 50, 45 44, 50 48 C 55 44, 64 50, 67 60 C 70 72, 62 82, 50 82 Z" fill={color} opacity="0.55" />
      <path d="M50 72 C 44 72, 40 66, 43 60 C 46 55, 50 55, 50 57 C 50 55, 54 55, 57 60 C 60 66, 56 72, 50 72 Z" fill={color} opacity="0.8" />
      {/* Stem + leaf */}
      <path d="M50 90 L 50 118" stroke="var(--lf-mint-deep)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <path d="M50 104 C 62 104, 68 96, 66 90 C 60 92, 52 98, 50 104 Z" fill="var(--lf-mint-deep)" />
    </svg>
  );
};

export const BotanicalBranch = ({ color = "var(--lf-mint-deep)", size = 160, className = "", style = {} }) => (
  <svg viewBox="0 0 60 200" width={size * 0.3} height={size} className={className} style={style} aria-hidden="true">
    <path d="M30 195 Q 32 100, 30 5" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />
    {[
      { y: 25, side: 1, size: 1.0 },
      { y: 55, side: -1, size: 0.85 },
      { y: 85, side: 1, size: 0.9 },
      { y: 115, side: -1, size: 0.95 },
      { y: 145, side: 1, size: 0.75 },
      { y: 170, side: -1, size: 0.7 },
    ].map((l, i) => (
      <g key={i}>
        <path
          d={`M30 ${l.y} C ${30 + l.side * 8 * l.size} ${l.y - 5 * l.size}, ${30 + l.side * 22 * l.size} ${l.y - 8 * l.size}, ${30 + l.side * 26 * l.size} ${l.y + 4 * l.size} C ${30 + l.side * 22 * l.size} ${l.y + 8 * l.size}, ${30 + l.side * 8 * l.size} ${l.y + 6 * l.size}, 30 ${l.y} Z`}
          fill={color}
          opacity="0.85"
        />
        <path
          d={`M30 ${l.y} L ${30 + l.side * 22 * l.size} ${l.y + 1}`}
          stroke="white"
          strokeWidth="0.5"
          opacity="0.4"
        />
      </g>
    ))}
  </svg>
);

export const Sparkle = ({ color = "var(--lf-gold-bright)", size = 28, className = "", style = {} }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} style={style} aria-hidden="true">
    <path
      d="M12 1 L 13.5 9 L 22 12 L 13.5 15 L 12 23 L 10.5 15 L 2 12 L 10.5 9 Z"
      fill={color}
    />
  </svg>
);

export const FivePetal = ({ color = "var(--lf-pink-deep)", size = 80, className = "", style = {} }) => (
  <svg
    viewBox="0 0 100 100"
    width={size}
    height={size}
    className={className}
    style={style}
    aria-hidden="true"
  >
    <g fill={color}>
      {[0, 72, 144, 216, 288].map((deg) => (
        <ellipse
          key={deg}
          cx="50"
          cy="26"
          rx="14"
          ry="22"
          transform={`rotate(${deg} 50 50)`}
        />
      ))}
    </g>
    <circle cx="50" cy="50" r="11" fill="var(--lf-yellow)" />
  </svg>
);

export const Leaf = ({ color = "var(--lf-mint-deep)", size = 100, className = "", style = {} }) => (
  <svg
    viewBox="0 0 50 100"
    width={size / 2}
    height={size}
    className={className}
    style={style}
    aria-hidden="true"
  >
    <path
      d="M25 100 C 25 100, 0 60, 25 0 C 50 60, 25 100, 25 100 Z"
      fill={color}
    />
    <path
      d="M25 8 L25 92"
      stroke="rgba(255,255,255,0.35)"
      strokeWidth="1.2"
      fill="none"
    />
  </svg>
);

export const Scribble = ({ className = "", color = "var(--lf-pink-deep)" }) => (
  <svg
    className={className}
    viewBox="0 0 200 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M2 8 Q 15 -2, 28 8 T 54 8 T 80 8 T 106 8 T 132 8 T 158 8 T 184 8 T 198 8"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

export const SquiggleArrow = ({ className = "", color = "var(--lf-gold-deep)", size = 120 }) => (
  <svg
    className={className}
    width={size}
    height={size * 0.25}
    viewBox="0 0 120 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M2 20 Q 20 2, 40 15 T 78 12 L 105 12"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
    />
    <path
      d="M96 5 L110 12 L96 20"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

/* Wavy full-width dividers.
   `fill` is the color of the *incoming* section (the shape shows underneath). */
export const WaveTop = ({ fill = "var(--lf-cream)", className = "" }) => (
  <svg
    className={`lf-wavy-svg ${className}`}
    viewBox="0 0 1440 100"
    preserveAspectRatio="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M0,60 C240,120 480,0 720,50 C960,100 1200,0 1440,60 L1440,0 L0,0 Z"
      fill={fill}
    />
  </svg>
);

export const WaveBottom = ({ fill = "var(--lf-cream)", className = "" }) => (
  <svg
    className={`lf-wavy-svg ${className}`}
    viewBox="0 0 1440 100"
    preserveAspectRatio="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M0,40 C240,-20 480,100 720,50 C960,0 1200,100 1440,40 L1440,100 L0,100 Z"
      fill={fill}
    />
  </svg>
);
